import { useCallback } from 'react'
import { BigNumber } from 'ethers'
import { parseUnits } from 'ethers/lib/utils'
import { getChainById } from 'symbiosis-js-sdk'

import { MaxButton } from '~/components/MaxButton'
import { SymbiosisInput } from '~/components/SymbiosisInput'
import { SIS_DECIMALS } from '~/lib/SISDecimals'
import { useWallet } from '~/lib/useWallet'
import { toAmount } from '~/lib/helpers'
import SISIcon from '~/assets/icons/sis.svg'

import { SymbiosisButton } from '../SymbiosisButton'

import { Balance } from './Balance'
import { useGetTestSIS } from './useGetTestSIS'

import styles from './SISAmountInput.pcss'
import { InputButton } from '~/components/InputButton'
import { useVESISParams } from '~/hooks/useVESISParams'
import { getSISUrl } from '~/lib/getSISUrl'

interface Props {
	balance?: BigNumber
	disabled?: boolean
	value: string
	onChange: (value: string) => void
}

export function SISAmountInput({
	balance,
	disabled,
	value,
	onChange: onChangeProps,
}: Props): JSX.Element {
	const { config } = useVESISParams()

	const wallet = useWallet()

	const { isAvailable, isReceiving, getTestSIS } = useGetTestSIS()

	const chain = getChainById(wallet.chainId)
	const balanceAmount = balance && toAmount(SIS_DECIMALS, balance)

	const onChange = useCallback(
		({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
			if (value === '') {
				onChangeProps('')

				return
			}

			if (value[0] === '0' && value[1] === '0') {
				return
			}

			if (value === '.' || value === ',') {
				onChangeProps('.')

				return
			}

			if (!value.includes('.') && value.includes(',')) {
				onChangeProps(value.replace(/,/, '.'))

				return
			}

			try {
				parseUnits(value, SIS_DECIMALS)
			} catch {
				return
			}

			onChangeProps(value)
		},
		[onChangeProps]
	)

	return (
		<SymbiosisInput
			className={styles.input}
			disabled={disabled}
			address={wallet.account}
			helperText={
				<div className={styles.helper}>
					<Balance hidden={!wallet.account} value={balanceAmount} />
					{isAvailable && (
						<SymbiosisButton
							className={styles.getTokens}
							disabled={isReceiving}
							type="button"
							variant="black"
							onClick={() => {
								getTestSIS()
							}}
						>
							{isReceiving ? 'Getting....' : 'Get tokens'}
						</SymbiosisButton>
					)}
				</div>
			}
			label="Amount"
			networkIcon={chain?.icons?.small}
			onChange={onChange}
			placeholder="Enter SIS Amount"
			startAdornment={<SISIcon className={styles.sisIcon} />}
			value={value}
			endAdornment={
				<>
					<InputButton
						onClick={() => {
							window.open(getSISUrl(config), '_blank')
						}}
					>
						Get {parseFloat(balanceAmount) > 0 ? 'more' : ''} SIS
					</InputButton>
					<MaxButton
						disabled={disabled || !balanceAmount}
						onClick={() => {
							if (!balanceAmount) {
								return
							}

							onChangeProps(balanceAmount)
						}}
					/>
				</>
			}
		/>
	)
}
